// Podium live talk: one speaker, translations shown side by side.
//
// Phone: what you're saying, then one lane per language with its newest line.
// Wide screens (or "Screen" mode on the presenter's laptop): a grid of large
// captions that people can read from the back of the room.

import { icons } from '../shared/icons.js';
import { playEarcon, primeEarcons } from '../shared/earcons.js';

const MAX_LINES = 6;

let currentSessionId = null;
let participantId = null;
let speakerLanguage = 'eng';
let sessionLanguages = [];
let isRecording = false;
let isStarting = false;
let sessionMessages = [];
let sessionCreatedOnBackend = false;
let messageTexts = {}; // messageId -> latest interim text
let history = {}; // language code -> final lines
let interim = {}; // language code -> interim text
let pending = {}; // language code -> set of message ids still being translated
let handlersRegistered = false;

let talkTimer = null;
let recordedMs = 0;
let recordingSince = 0;

const $ = (id) => document.getElementById(id);
const esc = (value) => window.podium.escapeHtml(value);
const nameOf = (code) => window.podium.languageName(code);

// ---------- Page lifecycle ----------

function initsession() {
    const setup = window.podium.getTalkSetup();
    speakerLanguage = setup.speaker;
    sessionLanguages = [setup.speaker, ...setup.targets];

    currentSessionId = null;
    participantId = null;
    sessionMessages = [];
    messageTexts = {};
    sessionCreatedOnBackend = false;
    isRecording = false;
    isStarting = false;
    recordedMs = 0;
    recordingSince = 0;
    history = {};
    interim = {};
    pending = {};
    sessionLanguages.forEach((code) => {
        history[code] = [];
        pending[code] = new Set();
    });

    render();
    bindControls();
    checkMicrophonePermission();
}

/** Called by the router when you navigate away from a live talk. */
async function leavesession() {
    if (isRecording) await stopRecording();
    clearInterval(talkTimer);
    talkTimer = null;
    exitProjector();
}

function render() {
    const page = $('page-session');
    const targets = sessionLanguages.filter((c) => c !== speakerLanguage);

    page.innerHTML = `
        <div class="pd-session" id="pd-session">
            <header class="pg-header pg-header--bar">
                <button type="button" class="pg-iconbtn" id="session-back-button" aria-label="End talk">${icons.back}</button>
                <div class="pg-header__title">
                    <h1>Live talk</h1>
                    <span class="pd-status"><span class="pd-status__dot" aria-hidden="true"></span><span id="pd-status-text">Ready</span></span>
                </div>
            </header>

            <div class="pg-banner pg-banner--error" id="mic-banner" role="alert" hidden style="margin: 12px 16px 0">
                <div class="pg-banner__body" id="mic-banner-text"></div>
            </div>

            <main class="pd-main">
                <section class="pd-speaker" aria-label="What you are saying">
                    <div class="pd-speaker__head">
                        <label class="pd-speaker__lang">
                            <span class="pg-sr-only">You are speaking</span>
                            <select id="speaker-language-select">
                                ${sessionLanguages.map((c) => `<option value="${c}"${c === speakerLanguage ? ' selected' : ''}>${esc(nameOf(c))} · you</option>`).join('')}
                            </select>
                        </label>
                        <span class="pg-bars" id="speaker-bars" aria-hidden="true" hidden><span></span><span></span><span></span><span></span></span>
                    </div>
                    <div class="pd-speaker__lines" id="speaker-transcript" aria-live="polite"></div>
                </section>

                <div class="pd-lanes-head">
                    <h2>On screen</h2>
                    <span class="pg-hint">${targets.length} ${targets.length === 1 ? 'language' : 'languages'}</span>
                </div>
                <div class="pd-lanes" id="translations-grid"></div>
            </main>

            <footer class="pd-dock">
                <button type="button" class="pd-dock__side pd-keep" id="screen-button" aria-pressed="false">${icons.screen}<span>Screen</span></button>
                <div class="pd-dock__center">
                    <button type="button" class="pd-mic" id="record-button" aria-label="Start listening">${icons.mic}</button>
                    <span class="pd-mic-label" id="record-button-text">Tap to start</span>
                </div>
                <button type="button" class="pd-dock__side" id="export-button">${icons.download}<span>Export</span></button>
            </footer>
        </div>`;

    renderSpeaker();
    renderLanes();
}

function bindControls() {
    $('session-back-button').addEventListener('click', () => window.showPage('page-landing'));
    $('export-button').addEventListener('click', exportSessionAsZip);
    $('screen-button').addEventListener('click', toggleProjector);

    $('record-button').addEventListener('click', async () => {
        primeEarcons();
        if (isStarting) return;
        if (isRecording) await stopRecording();
        else await startRecording();
    });

    $('speaker-language-select').addEventListener('change', (event) => {
        const next = event.target.value;
        if (next === speakerLanguage) return;
        speakerLanguage = next;
        if (window.websocketService?.isConnected()) {
            window.websocketService.updateParticipantLanguage(speakerLanguage);
        }
        renderSpeaker();
        renderLanes();
    });
}

// ---------- Rendering ----------

function linesHtml(code) {
    const finals = (history[code] || []).slice(-MAX_LINES);
    const live = interim[code];
    const out = finals.map((text, i) => {
        const current = !live && i === finals.length - 1;
        return `<p class="pd-line${current ? ' is-current' : ''}">${esc(text)}</p>`;
    });
    if (live) out.push(`<p class="pd-line is-current is-interim">${esc(live)}</p>`);
    return out.join('');
}

function renderSpeaker() {
    const box = $('speaker-transcript');
    if (!box) return;
    box.innerHTML = linesHtml(speakerLanguage)
        || '<p class="pd-placeholder">Tap the microphone and start speaking.</p>';
}

function laneState(code) {
    if (pending[code]?.size) return { state: 'working', label: 'Translating…' };
    if (history[code]?.length) return { state: 'done', label: 'Up to date' };
    return { state: 'idle', label: 'Waiting' };
}

function laneBodyHtml(code) {
    const lines = linesHtml(code);
    if (lines) return lines;
    if (pending[code]?.size) return '<div class="pd-skeleton" aria-hidden="true"><span></span><span></span></div>';
    return '<p class="pd-placeholder">Translations appear here.</p>';
}

function renderLanes() {
    const grid = $('translations-grid');
    if (!grid) return;
    grid.innerHTML = sessionLanguages
        .filter((c) => c !== speakerLanguage)
        .map((code) => {
            const s = laneState(code);
            return `<article class="pd-lane" id="translation-card-${code}" aria-label="${esc(nameOf(code))}">
                <div class="pd-lane__head">
                    <h3 class="pd-lane__name">${esc(nameOf(code))}</h3>
                    <span class="pd-tag" id="lane-tag-${code}" data-state="${s.state}">${s.label}</span>
                </div>
                <div class="pd-lane__lines" id="translation-text-${code}" aria-live="polite">${laneBodyHtml(code)}</div>
            </article>`;
        })
        .join('');
}

function updateLane(code) {
    if (code === speakerLanguage) {
        renderSpeaker();
        return;
    }
    const body = $(`translation-text-${code}`);
    const tag = $(`lane-tag-${code}`);
    if (!body || !tag) return;
    const s = laneState(code);
    tag.dataset.state = s.state;
    tag.textContent = s.label;
    body.innerHTML = laneBodyHtml(code);
}

function setLiveUi(live) {
    const session = $('pd-session');
    const button = $('record-button');
    const label = $('record-button-text');
    const bars = $('speaker-bars');
    if (!session || !button) return;
    session.classList.toggle('is-live', live);
    button.classList.toggle('is-live', live);
    button.innerHTML = live ? icons.pause : icons.mic;
    button.setAttribute('aria-label', live ? 'Pause listening' : (recordedMs ? 'Resume listening' : 'Start listening'));
    label.textContent = live ? 'Tap to pause' : (recordedMs ? 'Paused · tap to resume' : 'Tap to start');
    if (bars) bars.hidden = !live;
    updateStatus();
}

function formatElapsed(ms) {
    const total = Math.floor(ms / 1000);
    const m = Math.floor(total / 60);
    const s = String(total % 60).padStart(2, '0');
    return m >= 60 ? `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}:${s}` : `${String(m).padStart(2, '0')}:${s}`;
}

function updateStatus() {
    const el = $('pd-status-text');
    if (!el) return;
    const elapsed = recordedMs + (isRecording ? Date.now() - recordingSince : 0);
    if (isRecording) el.textContent = `Live · ${formatElapsed(elapsed)}`;
    else if (elapsed > 0) el.textContent = `Paused · ${formatElapsed(elapsed)}`;
    else el.textContent = 'Ready';
}

function showBanner(message) {
    const banner = $('mic-banner');
    if (!banner) return;
    $('mic-banner-text').textContent = message || '';
    banner.hidden = !message;
}

let toastEl = null;
let toastTimer = null;
function toast(message) {
    if (!toastEl) {
        toastEl = document.createElement('div');
        toastEl.className = 'pg-toast';
        toastEl.setAttribute('role', 'status');
        document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 2800);
}

// ---------- Projector ("Screen") ----------

function toggleProjector() {
    const session = $('pd-session');
    if (!session) return;
    if (session.classList.contains('is-projector')) exitProjector();
    else enterProjector();
}

function enterProjector() {
    const session = $('pd-session');
    const button = $('screen-button');
    session.classList.add('is-projector');
    button.setAttribute('aria-pressed', 'true');
    button.innerHTML = `${icons.exitScreen}<span>Exit</span>`;
    document.documentElement.requestFullscreen?.().catch(() => {});
}

function exitProjector() {
    const session = $('pd-session');
    const button = $('screen-button');
    session?.classList.remove('is-projector');
    if (button) {
        button.setAttribute('aria-pressed', 'false');
        button.innerHTML = `${icons.screen}<span>Screen</span>`;
    }
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
}

document.addEventListener('fullscreenchange', () => {
    if (!document.fullscreenElement && $('pd-session')?.classList.contains('is-projector')) exitProjector();
});

// ---------- Microphone ----------

async function checkMicrophonePermission() {
    try {
        if (!navigator.permissions?.query) return;
        const status = await navigator.permissions.query({ name: 'microphone' });
        const apply = () => {
            if (status.state === 'denied') {
                showBanner('Microphone access is blocked. Allow it in your browser settings, then try again.');
                $('record-button')?.setAttribute('disabled', '');
            } else {
                showBanner('');
                $('record-button')?.removeAttribute('disabled');
            }
        };
        apply();
        status.onchange = apply;
    } catch {
        // Permissions API not available; the browser will ask when we start.
    }
}

async function startRecording() {
    const { apiService, audioService, websocketService } = window;
    isStarting = true;
    $('record-button').disabled = true;
    $('record-button-text').textContent = 'Connecting…';

    try {
        const speakerName = window.googleAuthService?.getUserInfo()?.name || 'Speaker';

        if (!sessionCreatedOnBackend) {
            const sessionData = await apiService.createSession(
                'Podium Presentation',
                speakerName,
                sessionLanguages,
                false // no spoken translations for talks
            );
            if (!sessionData?.id) throw new Error('The server did not return a session.');
            currentSessionId = sessionData.id;
            sessionCreatedOnBackend = true;
        }

        await websocketService.connect();
        registerSocketHandlers();

        const participant = await websocketService.joinSession(currentSessionId, speakerName, speakerLanguage);
        participantId = participant.id;

        // Every time: cheap when nothing changed, and reopens the mic if iOS ended the track.
        await audioService.initialize();

        // Play the cue before opening the mic so it isn't recorded.
        await playEarcon('start');

        const serverInterval = websocketService?.serverConfig?.audioChunkIntervalMs ?? 100;
        const chunkIntervalMs = Math.max(serverInterval, audioService.getMinChunkIntervalMs());
        audioService.startRecording(
            (audioData, metadata) => {
                const isPauseBoundary = metadata && metadata.isPauseBoundary;
                if (!websocketService.isConnected()) return;
                if (audioService.hasVoiceActivity() || isPauseBoundary) {
                    websocketService.sendAudioChunk(audioData, isPauseBoundary, metadata || null);
                }
            },
            () => {},
            undefined,
            chunkIntervalMs
        );

        isRecording = true;
        recordingSince = Date.now();
        clearInterval(talkTimer);
        talkTimer = setInterval(updateStatus, 1000);
        showBanner('');
        setLiveUi(true);
    } catch (error) {
        console.error('Failed to start recording:', error);
        playEarcon('error');
        if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
            showBanner('Microphone access was denied. Allow it in your browser settings, then try again.');
        } else {
            showBanner(`Couldn't start: ${error?.message || error}`);
        }
        setLiveUi(false);
    } finally {
        isStarting = false;
        const button = $('record-button');
        if (button) button.disabled = false;
    }
}

async function stopRecording() {
    const { audioService, websocketService } = window;
    try {
        try {
            audioService.sendAudioOnPause();
        } catch (error) {
            console.error('Error sending audio on pause:', error);
        }
        if (websocketService?.isConnected()) {
            // A short silent chunk marked as a pause makes the server finish the sentence.
            websocketService.sendAudioChunk(new Uint8Array(1920), true);
        }
        await audioService.stopRecording();
        if (websocketService?.isConnected()) websocketService.setSpeakingStatus(false);
    } catch (error) {
        console.error('Failed to stop recording:', error);
    }

    if (isRecording) recordedMs += Date.now() - recordingSince;
    isRecording = false;
    clearInterval(talkTimer);
    talkTimer = null;
    interim[speakerLanguage] = '';
    renderSpeaker();
    setLiveUi(false);
    playEarcon('stop');
}

// ---------- Socket events ----------
// Registered once per page load; they always act on the current talk.

function registerSocketHandlers() {
    if (handlersRegistered) return;
    handlersRegistered = true;
    const ws = window.websocketService;

    ws.onTranscriptionProgress((data) => {
        const messageId = data.messageId || data.message_id;
        if (messageId && data.text) messageTexts[messageId] = data.text;
        interim[speakerLanguage] = data.text || '';
        renderSpeaker();
    });

    ws.onMessageComplete((data) => {
        const messageId = data.message_id || data.messageId || Date.now().toString();
        const text = data.text || '';
        interim[speakerLanguage] = '';
        if (text.trim()) {
            history[speakerLanguage] = [...(history[speakerLanguage] || []), text];
            sessionLanguages.forEach((code) => {
                if (code !== speakerLanguage) pending[code]?.add(messageId);
            });
        }
        renderSpeaker();
        sessionLanguages.forEach((code) => code !== speakerLanguage && updateLane(code));

        let message = sessionMessages.find((m) => m.id === messageId);
        if (!message) {
            message = { id: messageId, timestamp: new Date().toISOString(), sourceLanguage: speakerLanguage, sourceText: text, translations: {} };
            sessionMessages.push(message);
        }
        message.sourceText = text;
        message.translations[speakerLanguage] = text;
        autoSaveSession();
    });

    ws.onTranslationUpdate((data) => {
        const targetLang = data.targetLanguage || data.target_language;
        const translatedText = data.translatedText || data.translated_text || '';
        const messageId = data.messageId || data.message_id;
        if (!targetLang || !history[targetLang] || targetLang === speakerLanguage) return;

        if (translatedText.trim()) history[targetLang] = [...history[targetLang], translatedText];
        pending[targetLang]?.delete(messageId);
        updateLane(targetLang);

        let message = sessionMessages.find((m) => m.id === messageId);
        if (!message && messageId) {
            const sourceText = messageTexts[messageId] || '';
            message = { id: messageId, timestamp: new Date().toISOString(), sourceLanguage: speakerLanguage, sourceText, translations: {} };
            if (sourceText) message.translations[speakerLanguage] = sourceText;
            sessionMessages.push(message);
        }
        if (message) {
            message.translations[targetLang] = translatedText;
            autoSaveSession();
        }
    });
}

// ---------- Saving and export ----------

function autoSaveSession() {
    if (sessionMessages.length === 0 || !currentSessionId) return;
    try {
        const sessions = window.podium.getSavedSessions();
        const existingIndex = sessions.findIndex((s) => s.id === currentSessionId);
        const session = {
            id: currentSessionId,
            timestamp: existingIndex >= 0 ? sessions[existingIndex].timestamp : new Date().toISOString(),
            lastUpdated: new Date().toISOString(),
            speakerLanguage,
            languages: sessionLanguages,
            messages: sessionMessages,
            messageCount: sessionMessages.length
        };
        if (existingIndex >= 0) sessions[existingIndex] = session;
        else sessions.push(session);
        localStorage.setItem('podium-sessions', JSON.stringify(sessions));
    } catch (error) {
        console.error('Failed to auto-save session:', error);
    }
}

async function exportSessionAsZip() {
    if (sessionMessages.length === 0) {
        toast('Nothing to export yet. Speak first, then export.');
        return;
    }
    try {
        const zip = new JSZip();
        sessionLanguages.forEach((code) => {
            const lines = sessionMessages.map((m) => m.translations[code]).filter((t) => t && t.trim());
            if (lines.length) zip.file(`${nameOf(code)}.txt`, lines.join('\n\n'));
        });
        const blob = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `podium-talk-${new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast('Transcript downloaded');
    } catch (error) {
        console.error('Failed to export session:', error);
        toast("Couldn't export. Please try again.");
    }
}

function debugCheckSessions() {
    console.log(window.podium.getSavedSessions());
}

window.initsession = initsession;
window.leavesession = leavesession;
window.debugCheckSessions = debugCheckSessions;
